import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild, inject, isDevMode, signal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { SessionStorageService } from '../../../core/services/session-storage/session-storage.service';
import { PiasLayoutComponent } from '../../../shared/components/pias-layout/pias-layout.component';
import { SnackbarService } from '../../../shared/services/snackbar/snackbar.service';
import { ConfirmPopupComponent } from '../../agent-onboarding/new-rep-application/confirm-popup/confirm-popup.component';
import { AdStatusBadgeComponent } from '../marketing-review-components/ad-status-badge/ad-status-badge.component';
import { PinDraft, PinViewerComponent, pinViewerSupports } from '../marketing-review-components/pin-viewer/pin-viewer.component';
import { AD_ACCEPT_ATTRIBUTE, AdCommentPin, AdFileKind, adReviewComments, AdStatus, ChecklistItem, CLOSED_REVIEW_STATUSES, documents, MarketingAd, MarketingAdDetails, ReviewDecision } from '../marketing-review-interfaces/marketing-review.interface';
import { MarketingReviewService } from '../marketing-review-service/marketing-review.service';
import { HttpStatusCode } from 'axios';
import { ReviewAdModel } from './review-ad-models/review-ad.model';
import { ReviewAdService } from './review-ad-service/review-ad.service';
import { VersionCompareComponent } from '../marketing-review-components/version-compare/version-compare.component';
import { MatCheckboxModule } from '@angular/material/checkbox';

/** A top-level comment with the replies made to it, oldest first. */
type ThreadEntry = | { kind: 'comment'; key: string; at: string; comment: adReviewComments; replies: adReviewComments[] };

/** One uploaded version of a file — what the media viewer opens. */
interface MediaTab {
    label: string;
    fileName: string;
    url: any;
    kind: AdFileKind;
    meta: string;
    icon: string;
    tone: string;
    attachmentId: number;
    pinnable: boolean;
    groupKey: number;
    version: number;
    isCurrent: boolean;
    uploadedOn: string;
    openPins: number;
}

/** Every uploaded version of one file, oldest first, so a reviewer can follow its revisions. */
interface MediaGroup {
    key: number;
    versions: MediaTab[];
    current: MediaTab;
}

/**
 * Review page — the creative in a zoomable viewer on the left, the comment thread on the right,
 * with the decision actions in the header.
 *
 * Per the design, the reviewer records a decision with comments only; there is no per-criterion
 * checklist. `AdReview.criteria` is therefore saved empty on new reviews. Reviews raised before
 * this change (and the seeded mock data) still carry criteria, and any marked `No` are shown under
 * their comment card by `concerns()` — so historical detail is not lost.
 *
 * Reviews accumulate rather than overwrite, so the thread between a branch and the marketing team
 * is preserved on the ad. Opens read-only once a decision is final.
 */
@Component({
    selector: 'app-review-ad',
    imports: [
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        PiasLayoutComponent,
        AdStatusBadgeComponent,
        PinViewerComponent,
        VersionCompareComponent,
        MatCheckboxModule,
        ReactiveFormsModule,
    ],
    templateUrl: './review-ad.component.html',
    styleUrl: './review-ad.component.scss'
})
export class ReviewAdComponent implements OnInit {

    private router = inject(Router);
    private matDialog = inject(MatDialog);
    private fb = inject(FormBuilder);
    private snackBarService = inject(SnackbarService);
    private sessionStorageService = inject(SessionStorageService);
    private marketingReviewService = inject(MarketingReviewService);
    private adMarketingReviewService = inject(ReviewAdService);

    @ViewChild(PinViewerComponent) private pinViewer?: PinViewerComponent;
    @ViewChild('composerInput') private composerInput?: ElementRef<HTMLTextAreaElement>;

    protected readonly setSprinner = signal(false);

    protected notFound: boolean = false;
    protected adDetails!: MarketingAdDetails;
    protected adComments!: adReviewComments[];

    protected overallComments: string = '';
    protected commentsTouched: boolean = false;

    /** Id of the comment being edited inline, `null` when nothing is. */
    protected editingCommentId: number | null = null;
    protected editingText: string = '';

    /** Media viewer state. `mediaTabs` is `mediaGroups` flattened, group by group. */
    protected mediaGroups: MediaGroup[] = [];
    protected mediaTabs: MediaTab[] = [];
    protected activeMedia: number = 0;

    /** Compare mode — see `openCompare()`. Held by key so a rebuild of the groups is followed. */
    private compareKey: number | null = null;
    protected compareLeftId: number | null = null;
    protected compareRightId: number | null = null;
    protected zoom: number = 100;

    private readonly ZOOM_MIN: number = 50;
    private readonly ZOOM_MAX: number = 200;
    private readonly ZOOM_STEP: number = 25;

    /** New-version state — see `canUploadNewVersions`. */
    protected readonly ACCEPTED_FILE_TYPES: string = AD_ACCEPT_ATTRIBUTE;
    /** Files (by group key) given a new version on this visit. */
    protected revisedGroups: Set<number> = new Set<number>();

    actionFlag = signal(false);

    protected checklistItems: ChecklistItem[] = [];

    protected readonly isReviewer: boolean = this.marketingReviewService.isMarketingReviewer();

    /** Pin state — see the Pins section. */
    protected draftPin: AdCommentPin | null = null;
    protected activePinId: number | null = null;
    /** Pinned comments on the open tab, kept as a field so the viewer is not handed a new array each pass. */
    protected mediaPins: adReviewComments[] = [];
    /** Reply box text, per comment id. */
    protected replyText: Record<number, string> = {};
    data = history.state;
    applicationId!: number;
    public marketingReviewApproveformGroup: FormGroup = this.fb.group({
        ConcentCheck: [null, [Validators.required]],
    });

    ngOnInit(): void {
        this.applicationId = this.data.applicationId;
        if (this.isReviewer) {
            if (['Compliance'].includes(this.sessionStorageService.getCurrentUserRole())) {
                this.checklistItems = [
                    { id: 1, title: 'Whether products are mentioned in the articles.' },
                    { id: 2, title: 'The advertisement provides a fair and balanced view of the investment product to which it relates.' },
                    { id: 3, title: 'The advertisement is not false or misleading.' },
                    { id: 4, title: 'The advertisement presents information in a clear manner, whether in text or otherwise.' },
                    { id: 5, title: 'The advertisement includes the Representative’s name, MAS RNF number and Branch name.' },
                    { id: 6, title: 'The advertisement includes the necessary clauses.' },
                    { id: 7, title: 'EDM invites (e.g. webinar invites) include the speaker’s presentation slides.' },
                    { id: 8, title: 'For “FWD” brand/products, the word “forward” (in English or any other language) is not mentioned in any manner in any materials (hard copy or electronic), advertisements, offers, presentations, promotions or oral discussion of “FWD” branded products/services. This protects against possible legal action and protects the value of the “FWD” brand.' },
                ];
            }
            else if (['Partnership'].includes(this.sessionStorageService.getCurrentUserRole())) {
                this.checklistItems = [
                    { id: 1, title: 'Whether products are mentioned in the articles.' },
                    { id: 2, title: 'The advertisement provides a fair and balanced view of the investment product to which it relates.' },
                    { id: 3, title: 'The advertisement is not false or misleading.' },
                    { id: 4, title: 'For “FWD” brand/products, the word “forward” (in English or any other language) is not mentioned in any manner in any materials (hard copy or electronic), advertisements, offers, presentations, promotions or oral discussion of “FWD” branded products/services. This protects against possible legal action and protects the value of the “FWD” brand.' },
                    { id: 5, title: 'Approval has been obtained from the third party before using third-party trademarks and contact details in self-created marketing materials. These include distribution partners’ names, logos or service marks, addresses, telephone numbers, or any other information or materials relating to third parties.' },
                    { id: 6, title: 'Approval has been obtained from the respective insurers where the product name or insurer’s name is mentioned in materials on MediSave-approved Accident & Health products (e.g. integrated shield plan, non-integrated shield plan, supplements for CareShield Life and ElderShield).' },
                ];
            }
            else if (['Marketing'].includes(this.sessionStorageService.getCurrentUserRole())) {
                this.checklistItems = [
                    { id: 1, title: 'The advertisement includes the Representative’s name, MAS RNF number and Branch name.' },
                    { id: 2, title: 'The advertisement is clearly legible, follows the required font size, and upholds the corporate image and branding.' },
                    { id: 3, title: 'Approval has been obtained from the third party before using third-party trademarks and contact details in self-created marketing materials. These include distribution partners’ names, logos or service marks, addresses, telephone numbers, or any other information or materials relating to third parties.' },
                ];
            }

        }
        this.loadAd();
    }

    /**
     * Prefer the ad handed over in router state; fall back to the `id` query param so a hard
     * refresh on this URL still resolves.
     */
    private async loadAd(): Promise<void> {
        this.setSprinner.set(true);
        const responseData = await this.adMarketingReviewService.getAdById(this.applicationId)
            .then((response) => {
                return JSON.parse(response);
            })
            .catch((error) => {
                // console.error(error);
                return error;
            });
        this.setSprinner.set(false);
        if (responseData.status === HttpStatusCode.Ok) {
            this.adDetails = responseData.data.responsedata.MarketingReviewApplication;
            if (this.isReviewer) {
                if (['Compliance'].includes(this.sessionStorageService.getCurrentUserRole())) {
                  this.marketingReviewApproveformGroup.get('ConcentCheck')?.setValue(this.adDetails.complianceConsent);
                  if(!['','approved'].includes(this.adDetails.compliance)){  
                    this.actionFlag.set(true);  
                  }
                }
                else if (['Partnership'].includes(this.sessionStorageService.getCurrentUserRole())) {
                  this.marketingReviewApproveformGroup.get('ConcentCheck')?.setValue(this.adDetails.partnershipConsent);
                  if(!['','approved','not_required'].includes(this.adDetails.partnership)){
                    this.actionFlag.set(true);
                  }
                }
                else if (['Marketing'].includes(this.sessionStorageService.getCurrentUserRole())) {
                  this.marketingReviewApproveformGroup.get('ConcentCheck')?.setValue(this.adDetails.marketingConsent);
                  if(!['','approved'].includes(this.adDetails.marketing)){
                    this.actionFlag.set(true);
                  }
                }
            }
            this.rebuildThread();
            this.buildMediaTabs();

        } else {
            if (responseData.code === HttpStatusCode.BadRequest) {
                // Display notification on API failure.
                this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
            }
            if (responseData.code === HttpStatusCode.Unauthorized) {
                this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
                this.router.navigate(['/login']);
            }
        }
    }


    // ── Media viewer ────────────────────────────────────────────────────────────

    /**
     * One tab per uploaded version, clustered by file. A correction is uploaded as a new document
     * pointing back at the one it revises, so each file's chain is grouped on its first upload and
     * numbered in upload order. Tabs are laid out group by group, so a file's versions sit together.
     *
     * The open version survives a rebuild — one follows every comment — and otherwise defaults to
     * the current version of the first file.
     */
    private async buildMediaTabs(): Promise<void> {
        const selectedId: number | null | undefined = this.media?.attachmentId;
        const attachments: documents[] = this.adDetails.documents ?? [];
        const byId = new Map<number, documents>(attachments.map((attachment) => [attachment.documentId, attachment]));
        const chains = new Map<number, documents[]>();

        attachments.forEach((attachment) => {
            const root: number = this.rootDocumentIdOf(attachment, byId);
            chains.set(root, [...(chains.get(root) ?? []), attachment]);
        });

        const groups: MediaGroup[] = [];

        [...chains.entries()]
            .sort(([a], [b]) => a - b)
            .forEach(([key, chain]) => {
                // Ids are issued in upload order; `documentDate` may not be fine-grained enough to.
                chain.sort((a, b) => a.documentId - b.documentId);
                const versions: MediaTab[] = chain.map((attachment, index) =>
                    this.toMediaTab(attachment, key, index + 1, index === chain.length - 1));

                groups.push({ key, versions, current: versions[versions.length - 1] });
            });

        this.mediaGroups = groups;
        this.mediaTabs = groups.flatMap((group) => group.versions);

        const kept: number = this.mediaTabs.findIndex((tab) => tab.attachmentId === selectedId);
        this.activeMedia = kept > -1 ? kept : groups.length ? this.mediaTabs.indexOf(groups[0].current) : 0;
        this.refreshMediaPins();
    }

    /**
     * Walks `parentDocumentId` back to the first upload, so a chain groups the same whether the
     * backend points each correction at the root or at the version before it. A parent no longer on
     * the ad still keys the group, so its surviving versions stay together.
     */
    private rootDocumentIdOf(attachment: documents, byId: Map<number, documents>): number {
        const seen = new Set<number>([attachment.documentId]);
        let node: documents = attachment;

        while (node.groupId && !seen.has(node.groupId)) {
            const parent: documents | undefined = byId.get(node.groupId);
            if (!parent) {
                return node.groupId;
            }
            seen.add(parent.documentId);
            node = parent;
        }
        return node.documentId;
    }

    private toMediaTab(attachment: documents, groupKey: number, version: number, isCurrent: boolean): MediaTab {
        const kind: AdFileKind = this.adMarketingReviewService.fileKindOf(attachment.documentName);

        return {
            label: this.adMarketingReviewService.formatLabelOf(attachment.documentName),
            fileName: attachment.documentName,
            url: attachment.url,
            kind,
            meta: '',
            icon: this.adMarketingReviewService.iconOf(attachment.documentName),
            tone: this.adMarketingReviewService.toneOf(attachment.documentName),
            attachmentId: attachment.documentId,
            pinnable: pinViewerSupports(attachment.documentName, kind),
            groupKey,
            version,
            isCurrent,
            uploadedOn: attachment.documentDate,
            openPins: this.adComments?.filter((comment) =>
                comment.documentId === attachment.documentId && this.isOpenPin(comment)).length ?? 0
        };
    }

    protected get media(): MediaTab | null {
        return this.mediaTabs[this.activeMedia] ?? null;
    }

    /** The file the open version belongs to, with all its versions. */
    protected get activeGroup(): MediaGroup | null {
        return this.mediaGroups.find((group) => group.key === this.media?.groupKey) ?? null;
    }

    protected selectMedia(index: number): void {
        this.activeMedia = index;
        this.zoom = 100;
        this.refreshMediaPins();
    }

    protected selectVersion(tab: MediaTab): void {
        this.selectMedia(this.mediaTabs.indexOf(tab));
    }

    /** From an earlier version, back to the latest upload of the same file. */
    protected showCurrentVersion(): void {
        const group: MediaGroup | null = this.activeGroup;
        if (group) {
            this.selectVersion(group.current);
        }
    }

    // ── Version compare ─────────────────────────────────────────────────────────

    /** The file being compared, while compare mode is open. */
    protected get compareGroup(): MediaGroup | null {
        return this.compareKey === null ? null : this.mediaGroups.find((group) => group.key === this.compareKey) ?? null;
    }

    /**
     * Swaps the review body for a full-width, side-by-side view of two versions of one file. Opens
     * on `from` against the current version when an earlier version is given, otherwise on the
     * version before the current one; either side can then be switched to any version.
     */
    protected openCompare(group: MediaGroup, from?: MediaTab): void {
        if (group.versions.length < 2) {
            return;
        }
        const left: MediaTab = from && !from.isCurrent ? from : group.versions[group.versions.length - 2];
        this.compareKey = group.key;
        this.compareLeftId = left.attachmentId;
        this.compareRightId = group.current.attachmentId;
    }

    protected closeCompare(): void {
        this.compareKey = null;
    }

    protected zoomIn(): void {
        this.zoom = Math.min(this.ZOOM_MAX, this.zoom + this.ZOOM_STEP);
    }

    protected zoomOut(): void {
        this.zoom = Math.max(this.ZOOM_MIN, this.zoom - this.ZOOM_STEP);
    }

    protected get canZoomIn(): boolean {
        return this.zoom < this.ZOOM_MAX;
    }

    protected get canZoomOut(): boolean {
        return this.zoom > this.ZOOM_MIN;
    }

    // ── View state ──────────────────────────────────────────────────────────────

    /** A finalised decision cannot be re-reviewed from here. */
    protected get isReadOnly(): boolean {
        return !!this.adDetails && CLOSED_REVIEW_STATUSES.includes(this.normalizeAdStatus(this.adDetails.status));
    }

    /** Flags the composer only when a decision was attempted with nothing to go on. */
    protected get commentsInvalid(): boolean {
        return this.commentsTouched && !this.overallComments.trim() && !this.threadCount;
    }

    // ── Thread ──────────────────────────────────────────────────────────────────

    /**
     * Comments and decisions in one list, oldest first, so the conversation reads in order.
     *
     * Rebuilt whenever the ad changes rather than exposed as a getter — a getter would hand the
     * `@for` a freshly sorted array on every change-detection pass.
     */
    protected thread: ThreadEntry[] = [];

    private async rebuildThread(): Promise<void> {
        const responseData = await this.adMarketingReviewService.getAllCommentsById(this.applicationId)
            .then((response) => {
                return JSON.parse(response);
            })
            .catch((error) => {
                // console.error(error);
                return error;
            });
        this.setSprinner.set(false);
        if (responseData.status === HttpStatusCode.Ok) {
            // An ad with no comments comes back with no list at all.
            this.adComments = responseData.data.responsedata;
            this.adComments = this.adComments.filter(item => item.group_id == this.mediaTabs[this.activeMedia].groupKey);
            this.numberPins(this.adComments);
            this.thread = this.threadOf(this.adComments);
            this.buildMediaTabs();
            this.refreshMediaPins();

        } else {
            if (responseData.code === HttpStatusCode.BadRequest) {
                // Display notification on API failure.
                this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
            }
            if (responseData.code === HttpStatusCode.Unauthorized) {
                this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
                this.router.navigate(['/login']);
            }
        }
    }

    /**
     * The API returns comments and replies as one flat list; a reply is a comment whose `replyId`
     * names the comment it answers. Replies are hung under their top-level comment — following a
     * reply-to-a-reply up to it — so a pin's conversation reads in one card. A reply whose comment
     * has since been deleted stays in the thread on its own, quoting what it replied to.
     */
    private threadOf(comments: adReviewComments[]): ThreadEntry[] {
        const byId = new Map<number, adReviewComments>(comments.map((comment) => [comment.adId, comment]));
        const byTime = (a: adReviewComments, b: adReviewComments): number =>
            new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime();

        const rootOf = (comment: adReviewComments): adReviewComments => {
            const seen = new Set<number>([comment.adId]);
            let node: adReviewComments = comment;
            while (node.replyId && byId.has(node.replyId) && !seen.has(node.replyId)) {
                node = byId.get(node.replyId)!;
                seen.add(node.adId);
            }
            return node;
        };

        const replies = new Map<number, adReviewComments[]>();
        const topLevel: adReviewComments[] = [];

        comments.forEach((comment) => {
            const root: adReviewComments = rootOf(comment);
            if (root === comment) {
                topLevel.push(comment);
            } else {
                replies.set(root.adId, [...(replies.get(root.adId) ?? []), comment]);
            }
        });

        return topLevel
            .sort(byTime)
            .map((comment): ThreadEntry => ({
                kind: 'comment',
                key: `comment-${comment.adId}`,
                at: comment.createdDate,
                comment,
                replies: (replies.get(comment.adId) ?? []).sort(byTime)
            }));
    }

    /**
     * Fills in a badge number for any pinned comment the API returned without one, counting pins
     * in the order they were made, so the card and the material show the same number.
     *
     * ⚠️ Fallback only — unlike a server-issued `pinNo`, these shift when an earlier pin is deleted.
     * Confirm with the backend whether `pinNo` is returned.
     */
    private numberPins(comments: adReviewComments[]): void {
        let next: number = 1;
        [...comments]
            .filter((comment) => comment.pin)
            .sort((a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime())
            .forEach((comment) => {
                comment.pinNo = comment.pinNo || next;
                next = Math.max(next, comment.pinNo) + 1;
            });
    }

    protected get threadCount(): number {
        return this.thread.length;
    }

    /** Status arrives as sent — `resolved` from this page — so compare without regard to case. */
    protected isResolved(comment: adReviewComments): boolean {
        return (comment.status ?? '').toLowerCase() === 'resolved';
    }

    /**
     * Edit and delete are offered on your own comments only, and only while the review is still
     * open — once a decision is final the thread it was based on stays as it was.
     */
    protected canModify(comment: adReviewComments): boolean {
        return !this.isReadOnly && comment.commentedBy === this.currentUserName;
    }

    protected startEdit(comment: adReviewComments): void {
        this.editingCommentId = comment.adId;
        this.editingText = comment.text;
    }

    protected cancelEdit(): void {
        this.editingCommentId = null;
        this.editingText = '';
    }

    protected async saveEdit(comment: adReviewComments): Promise<void> {
        if (!this.adDetails || !this.editingText.trim()) {
            this.snackBarService.openWarningSnackBar('A comment cannot be left empty.');
            return;
        }
        let updateReviewComments = new ReviewAdModel();
        updateReviewComments.setCommentId(comment.adId);
        updateReviewComments.setCommentedBy(this.sessionStorageService.getCurrentUserName());
        updateReviewComments.setPiasRepCode(this.sessionStorageService.getCurrentRepCode());
        updateReviewComments.setDescription(this.editingText.toString());
        updateReviewComments.setDocumentId(this.mediaTabs[this.activeMedia].attachmentId);
        updateReviewComments.setUserRole(this.sessionStorageService.getCurrentUserRole());
        updateReviewComments.setGroupId(this.mediaTabs[this.activeMedia].groupKey);


        this.setSprinner.set(true);
        const responseData = await this.adMarketingReviewService.updateComment(this.applicationId, updateReviewComments)
            .then((response) => {
                return JSON.parse(response);
            })
            .catch((error) => {
                // console.error(error);
                return error;
            });

        if (responseData.status === HttpStatusCode.Ok) {
            this.snackBarService.openSuccessSnackBar('Comment Updated.');
            this.cancelEdit();
            this.rebuildThread();
        } else {
            if (responseData.code === HttpStatusCode.BadRequest) {
                // Display notification on API failure.
                this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
            }
            if (responseData.code === HttpStatusCode.Unauthorized) {
                this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
                this.router.navigate(['/login']);
            }
        }
        this.setSprinner.set(false);
    }

    protected deleteComment(comment: adReviewComments): void {
        this.matDialog
            .open(ConfirmPopupComponent, {
                width: '420px',
                data: {
                    message: 'Delete this comment?',
                    message1: 'It will be removed from the thread. The audit log keeps a record.',
                    hidecancel: false
                }
            })
            .afterClosed()
            .subscribe((result) => {
                if (result?.data) {
                    this.removeComment(comment);
                }
            });
    }

    private async removeComment(comment: adReviewComments): Promise<void> {
        this.setSprinner.set(true);
        const responseData = await this.adMarketingReviewService.deleteComment(this.applicationId, comment.adId)
            .then((response) => {
                return JSON.parse(response);
            })
            .catch((error) => {
                // console.error(error);
                return error;
            });

        if (responseData.status === HttpStatusCode.Ok) {
            this.snackBarService.openSuccessSnackBar('Comment deleted.');
            this.rebuildThread();
        } else {
            if (responseData.code === HttpStatusCode.BadRequest) {
                // Display notification on API failure.
                this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
            }
            if (responseData.code === HttpStatusCode.Unauthorized) {
                this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
                this.router.navigate(['/login']);
            }
        }
        this.setSprinner.set(false);
    }

    /**
     * Posts the composer's text as a comment, leaving the ad's status untouched. With a pin
     * waiting in the composer, the comment is pinned to it.
     */
    protected async postComment(): Promise<void> {
        this.commentsTouched = true;

        if (!this.adDetails || !this.overallComments.trim()) {
            this.snackBarService.openWarningSnackBar('Write a comment before sharing it.');
            return;
        }

        const pin: AdCommentPin | null = this.draftPin;

        let newReviewComments = new ReviewAdModel();
        newReviewComments.setCommentedBy(this.sessionStorageService.getCurrentUserName());
        newReviewComments.setPiasRepCode(this.sessionStorageService.getCurrentRepCode());
        newReviewComments.setDescription(this.overallComments.toString());
        newReviewComments.setDocumentId(this.mediaTabs[this.activeMedia].attachmentId);
        pin ? newReviewComments.setPin(pin) : '';
        newReviewComments.setUserRole(this.sessionStorageService.getCurrentUserRole());
        newReviewComments.setStatus('open');
        newReviewComments.setGroupId(this.mediaTabs[this.activeMedia].groupKey);

        this.setSprinner.set(true);
        const responseData = await this.adMarketingReviewService.addComment(this.applicationId, newReviewComments)
            .then((response) => {
                return JSON.parse(response);
            })
            .catch((error) => {
                // console.error(error);
                return error;
            });

        if (responseData.status === HttpStatusCode.Ok) {
            this.snackBarService.openSuccessSnackBar(pin ? 'Pinned comment shared with the branch.' : 'Comment shared with the branch.');
            this.overallComments = '';
            this.commentsTouched = false;
            this.draftPin = null;
            this.rebuildThread();
            this.refreshMediaPins();
        } else {
            if (responseData.code === HttpStatusCode.BadRequest) {
                // Display notification on API failure.
                this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
            }
            if (responseData.code === HttpStatusCode.Unauthorized) {
                this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
                this.router.navigate(['/login']);
            }
        }
        this.setSprinner.set(false);
    }

    protected get currentUserName(): string {
        return this.sessionStorageService.getCurrentUserName();
    }


    // ── Pins ────────────────────────────────────────────────────────────────────

    private refreshMediaPins(): void {
        const documentId: number | null | undefined = this.media?.attachmentId;
        this.mediaPins = !this.adComments || documentId === undefined
            ? []
            : this.adComments.filter((comment) => comment?.documentId === documentId);
    }

    protected get pinTotal(): number {
        return this.adComments?.filter((comment) => comment.pin).length ?? 0;
    }

    protected get pinOpen(): number {
        return this.adComments?.filter((comment) => this.isOpenPin(comment)).length ?? 0;
    }

    private isOpenPin(comment: adReviewComments): boolean {
        return !!comment.pin && !this.isResolved(comment);
    }

    /** The draft pin, but only on the tab it was made on. */
    protected get mediaDraft(): PinDraft | null {
        return this.draftPin && this.draftPin.documentId === this.media?.attachmentId ? this.draftPin : null;
    }

    /** The viewer placed a pin — hand it to the composer, which posts it with the comment. */
    protected onPinDraft(draft: PinDraft): void {
        if (!this.media) {
            return;
        }
        this.draftPin = { ...draft, documentId: this.media.attachmentId };
        this.activePinId = null;
        setTimeout(() => this.composerInput?.nativeElement.focus());
    }

    protected discardDraftPin(): void {
        this.draftPin = null;
    }

    /** A pin was clicked on the material — highlight its card in the thread. */
    protected onPinSelect(commentId: number): void {
        this.activePinId = commentId;
        document.getElementById(`thread-comment-${commentId}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    /** A card was clicked — open the file its pin is on and scroll the pin into view. */
    protected focusPin(comment: adReviewComments): void {
        if (!comment.pin) {
            return;
        }

        const index: number = this.mediaTabs.findIndex((tab) => tab.attachmentId === comment.documentId);
        if (index < 0) {
            // Superseded versions stay on the ad, so a missing file was removed outright.
            this.snackBarService.openWarningSnackBar('The file this comment was pinned to has since been removed.');
            return;
        }

        this.activePinId = comment.adId;
        if (index !== this.activeMedia) {
            this.selectMedia(index);
        }
        // After change detection has created or updated the viewer for that tab.
        setTimeout(() => this.pinViewer?.scrollToPin(comment.adId));
    }

    protected pinLocation(pin: AdCommentPin): string {
        return [pin.pageLabel, pin.cellRef, pin.kind === 'area' ? 'area' : null].filter(Boolean).join(' · ');
    }

    protected async setPinStatus(comment: adReviewComments, status: string): Promise<void> {
        if (!this.adDetails) {
            return;
        }

        this.setSprinner.set(true);

        let updateReviewCommentsStatus = new ReviewAdModel();
        updateReviewCommentsStatus.setCommentId(comment.adId);
        updateReviewCommentsStatus.setCommentedBy(this.sessionStorageService.getCurrentUserName());
        updateReviewCommentsStatus.setPiasRepCode(this.sessionStorageService.getCurrentRepCode());
        updateReviewCommentsStatus.setDescription(comment.text);
        updateReviewCommentsStatus.setStatus(status);
        updateReviewCommentsStatus.setUserRole(this.sessionStorageService.getCurrentUserRole());

        const responseData = await this.adMarketingReviewService.updateComment(this.applicationId, updateReviewCommentsStatus)
            .then((response) => {
                return JSON.parse(response);
            })
            .catch((error) => {
                // console.error(error);
                return error;
            });

        if (responseData.status === HttpStatusCode.Ok) {
            this.snackBarService.openSuccessSnackBar('Comment Status Updated.');
            this.rebuildThread();
        } else {
            if (responseData.code === HttpStatusCode.BadRequest) {
                // Display notification on API failure.
                this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
            }
            if (responseData.code === HttpStatusCode.Unauthorized) {
                this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
                this.router.navigate(['/login']);
            }
        }
        this.setSprinner.set(false);
    }

    protected async postReply(comment: adReviewComments): Promise<void> {
        const text: string = (this.replyText[comment.adId] ?? '').trim();
        if (!this.adDetails || !text) {
            return;
        }

        this.setSprinner.set(true);
        let replyReviewComments = new ReviewAdModel();
        replyReviewComments.setCommentedBy(this.sessionStorageService.getCurrentUserName());
        replyReviewComments.setPiasRepCode(this.sessionStorageService.getCurrentRepCode());
        replyReviewComments.setDescription(text.toString());
        replyReviewComments.setReplyId(comment.adId);
        replyReviewComments.setReplyComment(comment.text);
        replyReviewComments.setUserRole(this.sessionStorageService.getCurrentUserRole());
        replyReviewComments.setDocumentId(this.mediaTabs[this.activeMedia].attachmentId);
        replyReviewComments.setGroupId(this.mediaTabs[this.activeMedia].groupKey);
        replyReviewComments.setStatus('resolved');

        this.setSprinner.set(true);
        const responseData = await this.adMarketingReviewService.addComment(this.applicationId, replyReviewComments)
            .then((response) => {
                return JSON.parse(response);
            })
            .catch((error) => {
                // console.error(error);
                return error;
            });

        if (responseData.status === HttpStatusCode.Ok) {
            this.snackBarService.openSuccessSnackBar('Reply shared with the branch.');
            this.replyText[comment.adId] = '';
            this.rebuildThread();
        } else {
            if (responseData.code === HttpStatusCode.BadRequest) {
                // Display notification on API failure.
                this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
            }
            if (responseData.code === HttpStatusCode.Unauthorized) {
                this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
                this.router.navigate(['/login']);
            }
        }
        this.setSprinner.set(false);
    }

    protected initialsOf(name: string): string {
        const parts: string[] = name.trim().split(/\s+/).filter(Boolean);
        if (!parts.length) {
            return '–';
        }
        const first: string = parts[0][0] ?? '';
        const last: string = parts.length > 1 ? parts[parts.length - 1][0] ?? '' : '';
        return (first + last).toUpperCase();
    }

    // ── New versions (returned ads) ─────────────────────────────────────────────

    /**
     * A returned ad's submitter answers the requested changes by uploading a new version of the
     * files that need it — files are never removed or swapped, and earlier versions stay on record.
     * Returned only — a rejection closes the submission. Submitter only, matched by name the same
     * way `canModify()` gates comments: a reviewer must not revise the branch's creative.
     */
    protected get canUploadNewVersions(): boolean {
        return !!this.adDetails && this.normalizeAdStatus(this.adDetails.status) === 'Returned' && this.adDetails.submittedBy === this.currentUserName;
    }

    /**
     * Uploads the picked file as the next version of `group`, linked to its current version, then
     * reloads the ad and opens the new version so the submitter can check it.
     */
    protected async onNewVersionSelected(event: Event, group: MediaGroup): Promise<void> {
        const input = event.target as HTMLInputElement;
        const file: File | undefined = input.files?.[0];
        // Reset so re-picking the same file still fires a change event.
        input.value = '';
        if (!file || !this.adDetails) {
            return;
        }

        const { accepted, wrongType, tooLarge } = this.marketingReviewService.screenAdFiles([file]);
        if (wrongType.length) {
            this.snackBarService.openWarningSnackBar(`${file.name} is not an accepted format.`);
            return;
        }
        if (tooLarge.length) {
            this.snackBarService.openWarningSnackBar(
                `${file.name} exceeds the ${this.marketingReviewService.MAX_FILE_SIZE_MB}MB limit `
                + `(${this.marketingReviewService.MAX_VIDEO_SIZE_MB}MB for video).`
            );
            return;
        }

        this.setSprinner.set(true);
        const replacementDocumentId = group.current.attachmentId?.toString();
        const responseData = await this.adMarketingReviewService.uploadAdAsset(
            this.applicationId.toString(),
            accepted[0],
            replacementDocumentId
        )
            .then((response) => {
                return JSON.parse(response);
            })
            .catch((error) => {
                // console.error(error);
                return error;
            });
        this.setSprinner.set(false);

        if (responseData.status === HttpStatusCode.Ok) {
            this.revisedGroups.add(group.key);
            this.snackBarService.openSuccessSnackBar(`New version of ${group.current.fileName} uploaded.`);
            await this.loadAd();
            this.showLatestVersionOf(group.key);
        } else {
            if (responseData.code === HttpStatusCode.BadRequest) {
                // Display notification on API failure.
                this.snackBarService.openFailureSnackBar("We couldn’t upload that file. Please try again");
            }
            if (responseData.code === HttpStatusCode.Unauthorized) {
                this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
                this.router.navigate(['/login']);
            }
        }
    }

    private showLatestVersionOf(groupKey: number): void {
        const group: MediaGroup | undefined = this.mediaGroups.find((g) => g.key === groupKey);
        if (group) {
            this.selectVersion(group.current);
        }
    }

    /**
     * Sends the ad back for review. Nothing stops a resubmission without a new upload on this visit
     * — the versions may have gone up on an earlier one — but the confirmation says so.
     */
    protected resubmitWithNewVersions(): void {
        const revised: number = this.revisedGroups.size;
        const message1: string = revised
            ? `New versions of ${revised} ${revised === 1 ? 'file' : 'files'} go to the reviewer. Earlier versions stay on record.`
            : 'You haven’t uploaded a new version on this visit. Resubmit the advertisement as it is?';

        this.matDialog
            .open(ConfirmPopupComponent, {
                width: '440px',
                data: {
                    message: 'Resubmit for review?',
                    message1,
                    hidecancel: false
                }
            })
            .afterClosed()
            .subscribe((result) => {
                if (result?.data) {
                    this.saveResubmission();
                }
            });
    }

    private async saveResubmission(): Promise<void> {
        if (!this.adDetails) {
            return;
        }

        this.setSprinner.set(true);
        const responseData = await this.adMarketingReviewService.resubmitWithNewMaterial(this.applicationId)
            .then((response) => {
                return JSON.parse(response);
            })
            .catch((error) => {
                // console.error(error);
                return error;
            });

        if (responseData.status === HttpStatusCode.Ok) {
            this.snackBarService.openSuccessSnackBar('New material submitted for review.');
            this.router.navigate(['/marketing-review']);
        } else {
            if (responseData.code === HttpStatusCode.BadRequest) {
                // Display notification on API failure.
                this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
            }
            if (responseData.code === HttpStatusCode.Unauthorized) {
                this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
                this.router.navigate(['/login']);
            }
        }
        this.setSprinner.set(false);
    }

    // ── Decisions ───────────────────────────────────────────────────────────────

    protected approve(): void {
        this.confirmAndSubmit(
            'Approved',
            'Approve this advertisement?',
            'The branch will be notified that it is cleared for publication.'
        );
    }

    protected returnForChanges(): void {
        this.confirmAndSubmit(
            'Returned',
            'Return this advertisement for changes?',
            'The branch will see your comments and can resubmit an amended version.'
        );
    }

    protected reject(): void {
        this.confirmAndSubmit(
            'Rejected',
            'Reject this advertisement?',
            'This closes the submission. The branch will need to raise a new advertisement.'
        );
    }

    private confirmAndSubmit(decision: ReviewDecision, message: string, message1: string): void {
        this.commentsTouched = true;

        /*
         * A decision still has to be explained, but the explanation can already be in the thread:
         * a reviewer who has posted their concerns as comments should not have to retype them into
         * the composer to press Approve.
         */
        if (!this.overallComments.trim() && !this.threadCount) {
            this.snackBarService.openWarningSnackBar('Please share your comments before recording a decision.');
            return;
        }

        this.matDialog
            .open(ConfirmPopupComponent, {
                width: '440px',
                data: { message, message1, hidecancel: false }
            })
            .afterClosed()
            .subscribe((result) => {
                if (result?.data) {
                    if (decision == 'Approved') {
                        if (this.marketingReviewApproveformGroup.get('ConcentCheck')?.value) {
                            this.submitReview(decision);
                        } else {
                            this.snackBarService.openWarningSnackBar('Please check the Acknowledgement');
                            this.marketingReviewApproveformGroup.get('ConcentCheck')?.markAsTouched();
                        }
                    } else {
                        this.submitReview(decision);
                    }
                }
            });
    }

    private async submitReview(decision: ReviewDecision): Promise<void> {
        if (!this.adDetails) {
            return;
        }

        this.setSprinner.set(true);
        const responseData = await this.adMarketingReviewService.marketingReviewStatusUpdate(this.applicationId, decision.toLowerCase())
            .then((response) => {
                return JSON.parse(response);
            })
            .catch((error) => {
                // console.error(error);
                return error;
            });

        if (responseData.status === HttpStatusCode.Ok) {
            this.snackBarService.openSuccessSnackBar(`Review recorded — advertisement ${decision.toLowerCase()}.`);
            this.router.navigate(['/marketing-review']);
        } else {
            if (responseData.code === HttpStatusCode.BadRequest) {
                // Display notification on API failure.
                this.snackBarService.openFailureSnackBar("We couldn’t process your request. Please try again");
            }
            if (responseData.code === HttpStatusCode.Unauthorized) {
                this.snackBarService.openFailureSnackBar("Session timed out. Try logging in again!");
                this.router.navigate(['/login']);
            }
        }
        this.setSprinner.set(false);
    }

    protected goBack(): void {
        this.router.navigate(['/marketing-review']);
    }

    protected normalizeAdStatus(value: string): AdStatus {
        const normalized = (value || '')
            .trim()
            .toLowerCase()
            .replace(/[\s-]+/g, '_');

        switch (normalized) {
            case 'draft':
                return 'Draft';

            case 'submitted':
            case 'pending':
            case 'pending_review':
                return 'Submitted';

            case 'in_review':
            case 'under_review':
                return 'In_Review';

            case 'approved':
                return 'Approved';

            case 'returned':
            case 'changesrequested':
                return 'Returned';

            case 'rejected':
                return 'Rejected';

            default:
                // Verify this fallback against the backend's actual status values.
                return 'Submitted';
        }
    }
}
